import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Download, FileText, Loader2 } from 'lucide-react';
import ModalShell from '../ui/ModalShell';
import ZoomPanViewer from '../ui/ZoomPanViewer';
import { useTheme } from '../../contexts/ThemeContext';
import { nactiPdfjs } from '../../lib/pdfjs';
import { ACCENT as SECTION_COLOR } from '../../lib/uiTokens';

/**
 * Doklad na celou obrazovku — sken, do kterého se dá zajet.
 *
 * Náhled v třetím sloupci je široký pár set pixelů. Na to, aby člověk viděl,
 * *že* doklad je od koho má být, to stačí; na přečtení variabilního symbolu,
 * rozpisu DPH nebo rukou dopsané poznámky ne. Ťuknutí na náhled proto otevře
 * doklad ve velkém a nechá ho přiblížit — prstem, kolečkem s Ctrl i tlačítky.
 *
 * **Přiblížení překresluje, nenafukuje.** `ZoomPanViewer` zvětšuje obsah CSS
 * transformem, což roztáhne pixely, které canvas už má — text by se při 3×
 * rozmazal přesně ve chvíli, kdy ho jde člověk luštit. Canvas si proto drží
 * svoji velikost na obrazovce a mění jen **rozlišení podkladu**: při zoomu 3×
 * se stránka vykreslí třikrát jemněji a transform pak zvětšuje ostrý obrázek.
 * Překresluje se až po doznění gesta (`PRODLEVA_MS`), aby pinch neházel
 * rendery, a jen do `MAX_PODKLAD_PX`, aby velké PDF nevyrobilo canvas,
 * který prohlížeč odmítne alokovat.
 */

/** Jak dlouho po poslední změně zoomu se čeká, než se překreslí (ms). */
const PRODLEVA_MS = 180;
/** Strop šířky podkladového canvasu v px — nad ním prohlížeče alokaci odmítají. */
const MAX_PODKLAD_PX = 5200;
/** Kolikrát jemněji se kreslí i bez přiblížení (ostré na retina displejích). */
const ZAKLADNI_OSTROST = 1.5;

const stahnoutUrl = (dokladId, p) => `/api/finance/doklady/${dokladId}/prilohy/${p.id}/stahnout`;

export default function DokladNahledModal({ prilohy = [], dokladId, onClose }) {
    const { isDark } = useTheme();

    const pdf = prilohy.find(p => p.mime_type === 'application/pdf');
    const obrazky = prilohy.filter(p => p.mime_type?.startsWith('image/'));
    const soubor = pdf || obrazky[0] || prilohy[0];

    return (
        <ModalShell
            open
            onClose={onClose}
            isDark={isDark}
            sirka={820}
            nadpis={soubor?.puvodni_nazev || 'Sken dokladu'}
            ikona={FileText}
            barva={SECTION_COLOR}
            paticka={soubor && (
                <div className="flex items-center gap-3">
                    <span className="text-[11px] flex-1" style={{ color: 'var(--text-tertiary)' }}>
                        Kolečkem, dvěma prsty nebo dvojklikem přiblížíš, tažením posuneš.
                    </span>
                    <a href={stahnoutUrl(dokladId, soubor)} target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold flex-shrink-0"
                        style={{ color: SECTION_COLOR, border: `1px solid ${SECTION_COLOR}44`, background: `${SECTION_COLOR}14` }}>
                        <Download size={13} /> Stáhnout
                    </a>
                </div>
            )}
        >
            {pdf
                ? <PdfProhlizec url={stahnoutUrl(dokladId, pdf)} isDark={isDark} />
                : obrazky.length > 0
                    ? <ObrazkyProhlizec prilohy={obrazky} dokladId={dokladId} isDark={isDark} />
                    : <Prazdno />}
        </ModalShell>
    );
}

// ── PDF ──────────────────────────────────────────────────────────────────────

function PdfProhlizec({ url, isDark }) {
    const canvasRef = useRef(null);
    const plochaRef = useRef(null);
    const renderTaskRef = useRef(null);

    const [pdfDoc, setPdfDoc] = useState(null);
    const [strana, setStrana] = useState(1);
    const [stran, setStran] = useState(0);
    const [kresli, setKresli] = useState(false);
    const [chyba, setChyba] = useState(null);

    // Živé měřítko z gest (překresluje se až usadené — viz `ostrost`).
    const [zoom, setZoom] = useState(1);
    const [ostrost, setOstrost] = useState(1);
    // Otočení tabletu změní plochu; bez překreslení by stránka zůstala
    // vysázená na starou šířku a půlka by čouhala ven.
    const [rozmer, setRozmer] = useState(0);

    useEffect(() => {
        let zruseno = false;
        // Nová adresa = čistý start (stejný důvod jako u malého náhledu
        // v `DokladNahled` — chyba z předchozího pokusu nesmí přebít dokument,
        // který se načte správně).
        setChyba(null);
        setPdfDoc(null);
        setStran(0);
        (async () => {
            try {
                const pdfjsLib = await nactiPdfjs();
                const doc = await pdfjsLib.getDocument(url).promise;
                if (zruseno) return;
                setPdfDoc(doc);
                setStran(doc.numPages);
                setStrana(1);
            } catch (err) {
                console.error('PDF load failed:', err);
                if (zruseno) return;
                setChyba(err?.status === 404 ? 'Soubor přílohy na serveru chybí.'
                    : err?.status === 403 ? 'K této příloze nemáte přístup.'
                    : err?.message || 'Neznámá chyba.');
            }
        })();
        return () => { zruseno = true; };
    }, [url]);

    // Zoom se mění při každém pohybu prstu; překreslovat na každý by znamenalo
    // desítky renderů za gesto. Čeká se, až se měřítko usadí.
    useEffect(() => {
        const t = setTimeout(() => setOstrost(zoom), PRODLEVA_MS);
        return () => clearTimeout(t);
    }, [zoom]);

    useEffect(() => {
        let t = null;
        const naZmenu = () => {
            clearTimeout(t);
            t = setTimeout(() => setRozmer(n => n + 1), PRODLEVA_MS);
        };
        window.addEventListener('resize', naZmenu);
        window.addEventListener('orientationchange', naZmenu);
        return () => {
            clearTimeout(t);
            window.removeEventListener('resize', naZmenu);
            window.removeEventListener('orientationchange', naZmenu);
        };
    }, []);

    useEffect(() => {
        if (!pdfDoc || !canvasRef.current) return;
        let zruseno = false;
        setKresli(true);
        (async () => {
            try {
                const page = await pdfDoc.getPage(strana);
                const canvas = canvasRef.current;
                const plocha = plochaRef.current;
                if (!canvas || !plocha || zruseno) return;

                const zaklad = page.getViewport({ scale: 1 });
                // Stránka se vejde do plochy celá — na výšku i na šířku. Fit jen
                // na šířku by u účtenky (úzká a dlouhá) nechal vidět proužek
                // nahoře a zbytek by se musel scrollovat, což se s panováním
                // uvnitř zoomu pere.
                const mistoW = plocha.clientWidth || 800;
                const mistoH = plocha.clientHeight || 600;
                const fit = Math.min(mistoW / zaklad.width, mistoH / zaklad.height);

                const dpr = window.devicePixelRatio || 1;
                const chteneW = zaklad.width * fit * dpr * ZAKLADNI_OSTROST * ostrost;
                const strop = Math.min(1, MAX_PODKLAD_PX / chteneW);
                const viewport = page.getViewport({
                    scale: fit * dpr * ZAKLADNI_OSTROST * ostrost * strop,
                });

                canvas.width = Math.round(viewport.width);
                canvas.height = Math.round(viewport.height);
                // CSS velikost zůstává „fit" — zvětšuje se jen podklad.
                canvas.style.width = `${Math.round(zaklad.width * fit)}px`;
                canvas.style.height = `${Math.round(zaklad.height * fit)}px`;

                if (renderTaskRef.current) {
                    try { renderTaskRef.current.cancel(); } catch { /* noop */ }
                }
                const task = page.render({ canvasContext: canvas.getContext('2d'), viewport });
                renderTaskRef.current = task;
                await task.promise;
            } catch (err) {
                if (err?.name !== 'RenderingCancelledException') console.error(err);
            }
            if (!zruseno) setKresli(false);
        })();
        return () => { zruseno = true; };
    }, [pdfDoc, strana, ostrost, rozmer]);

    // Šipkami se listuje, ať se nemusí mířit na tlačítka. Esc řeší ModalShell.
    useEffect(() => {
        const naKlavesu = (e) => {
            if (e.key === 'ArrowRight' || e.key === 'PageDown') setStrana(s => Math.min(stran, s + 1));
            if (e.key === 'ArrowLeft' || e.key === 'PageUp') setStrana(s => Math.max(1, s - 1));
        };
        document.addEventListener('keydown', naKlavesu);
        return () => document.removeEventListener('keydown', naKlavesu);
    }, [stran]);

    const zmenZoom = useCallback((s) => setZoom(s), []);

    if (chyba) return <Chyba text={chyba} url={url} />;

    if (!pdfDoc) {
        return (
            <div style={{ ...plochaStyl(isDark), display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                <Loader2 size={20} className="animate-spin" style={{ color: 'var(--text-tertiary)' }} />
                <span className="text-sm" style={{ color: 'var(--text-tertiary)' }}>Načítám PDF…</span>
            </div>
        );
    }

    return (
        <div style={{ position: 'relative' }}>
            <div ref={plochaRef} style={plochaStyl(isDark)}>
                <ZoomPanViewer resetKey={strana} onScaleChange={zmenZoom} kolecko="vzdy">
                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <canvas ref={canvasRef} style={{ display: 'block', boxShadow: '0 2px 18px rgba(0,0,0,0.28)' }} />
                    </div>
                </ZoomPanViewer>

                {kresli && (
                    <div style={{ position: 'absolute', top: 10, left: 10, pointerEvents: 'none' }}>
                        <Loader2 size={16} className="animate-spin" style={{ color: 'rgba(255,255,255,0.7)' }} />
                    </div>
                )}
            </div>

            {stran > 1 && <Listovani strana={strana} stran={stran} onZmena={setStrana} />}
        </div>
    );
}

// ── Obrázky (starší doklady, než na ně dojde sloučení do PDF) ────────────────

function ObrazkyProhlizec({ prilohy, dokladId, isDark }) {
    const [idx, setIdx] = useState(0);
    const p = prilohy[Math.min(idx, prilohy.length - 1)];
    const url = stahnoutUrl(dokladId, p);

    return (
        <div style={{ position: 'relative' }}>
            <div style={plochaStyl(isDark)}>
                <ZoomPanViewer resetKey={url} kolecko="vzdy">
                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <img src={url} alt={p.puvodni_nazev} draggable={false}
                            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', boxShadow: '0 2px 18px rgba(0,0,0,0.28)' }} />
                    </div>
                </ZoomPanViewer>
            </div>
            {prilohy.length > 1 && (
                <Listovani strana={idx + 1} stran={prilohy.length} onZmena={(n) => setIdx(n - 1)} />
            )}
        </div>
    );
}

// ── Kousky ───────────────────────────────────────────────────────────────────

/**
 * Plocha prohlížeče. Výška je z výřezu, ne ze `100 %` — tělo `ModalShell` se
 * měří podle obsahu (a strop má na `85vh`), takže procento by se nemělo o co
 * opřít a plocha by zkolabovala na výšku canvasu.
 */
const plochaStyl = (isDark) => ({
    position: 'relative',
    // Na výšku 70 dvh + hlavička + patička se vejde do stropu `ModalShellu`
    // (85 vh) i na nízkém notebooku; víc a tělo modalu by začalo scrollovat
    // pod nohama panování uvnitř dokumentu.
    //
    // Šířka modalu je schválně skromná (820): účtenky i faktury jsou na výšku,
    // takže se stránka vejde celá a rozhoduje výška. Na širokém modalu z ní
    // zbyl úzký proužek uprostřed tmavé plochy — velký modal, malý doklad.
    height: 'min(70dvh, 940px)',
    background: isDark ? 'rgba(0,0,0,0.38)' : 'rgba(0,0,0,0.06)',
    overflow: 'hidden',
});

function Listovani({ strana, stran, onZmena }) {
    const btn = (vypnuto) => ({
        width: 34, height: 34, borderRadius: 8,
        border: '1px solid rgba(255,255,255,0.12)',
        background: vypnuto ? 'rgba(0,0,0,0.35)' : 'rgba(0,0,0,0.6)',
        color: vypnuto ? 'rgba(255,255,255,0.35)' : '#fff',
        cursor: vypnuto ? 'default' : 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
    });

    // Plovoucí nad plochou, ne pod ní: patička by ukrojila z výšky dokumentu
    // a u jednostránkového dokladu by tam stála zbytečně.
    return (
        <div style={{
            position: 'absolute', left: '50%', bottom: 12, transform: 'translateX(-50%)',
            display: 'flex', alignItems: 'center', gap: 10, zIndex: 3,
        }}>
            <button type="button" onClick={() => onZmena(Math.max(1, strana - 1))}
                disabled={strana <= 1} aria-label="Předchozí strana" style={btn(strana <= 1)}>
                <ChevronLeft size={17} />
            </button>
            <span style={{
                fontSize: 12, fontWeight: 700, color: '#fff', minWidth: 54, textAlign: 'center',
                fontVariantNumeric: 'tabular-nums', background: 'rgba(0,0,0,0.6)',
                borderRadius: 999, padding: '6px 10px',
                backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
            }}>
                {strana} / {stran}
            </span>
            <button type="button" onClick={() => onZmena(Math.min(stran, strana + 1))}
                disabled={strana >= stran} aria-label="Další strana" style={btn(strana >= stran)}>
                <ChevronRight size={17} />
            </button>
        </div>
    );
}

function Chyba({ text, url }) {
    return (
        <div className="text-center" style={{ padding: '48px 16px' }}>
            <FileText size={22} className="mx-auto mb-2" style={{ color: 'var(--text-tertiary)' }} />
            <div className="text-sm" style={{ color: 'var(--text-primary)' }}>Náhled PDF se nepodařilo načíst</div>
            <div className="text-xs mt-1" style={{ color: 'var(--text-tertiary)' }}>{text}</div>
            <a href={url} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1.5 mt-3 px-3 py-1.5 rounded-lg text-xs font-semibold"
                style={{ color: 'var(--text-secondary)', border: '1px solid var(--border-default)' }}>
                <Download size={13} /> Otevřít soubor
            </a>
        </div>
    );
}

function Prazdno() {
    return (
        <div className="text-center text-sm" style={{ padding: '48px 16px', color: 'var(--text-tertiary)' }}>
            Doklad nemá přílohu k zobrazení.
        </div>
    );
}
